import { useContext } from 'react';
import { css } from '@emotion/core';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';
import Icon from '@veeva/corgix/icon';
import BlockingNavigationDialog from '../../../components/BlockingNavigationDialog';
import useMessageService from '../../../services/i18n/useMessageService';
import MyAccountLayout from '../../../components/MyAccountLayout/MyAccountLayout';
import PasswordField from '../../../components/PasswordField';
import Button from '../../../components/Button';
import PasswordRequirementsValidator from '../../../components/PasswordRequirementsValidator';
import NavigationTabs from '../../../constants/NavigationTabs';
import ChangePasswordContext from './ChangePasswordContext';
import getTargetAttribute from '../../../services/getTargetAttribute';

function ChangePasswordDesktop() {
    const {
        confirmPasswordError,
        currentPassword,
        currentPasswordError,
        disableSubmitButton,
        newPassword,
        newPasswordConfirm,
        newPasswordError,
        onCurrentPasswordChange,
        onNewPasswordChange,
        onNewPasswordConfirmChange,
        onSubmitButtonClick,
        onValidatorStateChange,
        showSuccessView,
    } = useContext(ChangePasswordContext);
    const [
        [
            savePasswordLabel = 'Save Your Password',
            currentPasswordLabel = 'Current Password',
            newPasswordLabel = 'New Password',
            retypePasswordLabel = 'Confirm New Password',
            changeSuccessLabel = 'Your password has been updated.',
        ],
    ] = useMessageService([
        'password.save_password',
        'password.current_password',
        'password.new_password',
        'password.retype_password',
        'password.change_success',
    ]);

    return (
        <MyAccountLayout selectedTab={NavigationTabs.CHANGE_PASSWORD}>
            <BlockingNavigationDialog
                when={!!currentPassword || !!newPassword || !!newPasswordConfirm}
            />
            {showSuccessView && (
                <div
                    css={({ green }) => {
                        return css`
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            gap: 1.667rem;

                            svg[data-icon='check-circle'] {
                                width: 8.333rem;
                                height: 8.333rem;
                                color: ${green};
                            }
                        `;
                    }}
                    data-target={getTargetAttribute(
                        'my-account-change-password-confirmation-description',
                    )}
                >
                    {changeSuccessLabel}
                    <Icon type={faCheckCircle} />
                </div>
            )}
            {!showSuccessView && (
                <>
                    <PasswordField
                        onChange={onCurrentPasswordChange}
                        value={currentPassword}
                        placeholder={currentPasswordLabel}
                        errorMessage={currentPasswordError}
                    />
                    <PasswordField
                        onChange={onNewPasswordChange}
                        value={newPassword}
                        placeholder={newPasswordLabel}
                        errorMessage={newPasswordError}
                        error={!!confirmPasswordError}
                    />
                    <PasswordField
                        onChange={onNewPasswordConfirmChange}
                        value={newPasswordConfirm}
                        placeholder={retypePasswordLabel}
                        errorMessage={confirmPasswordError}
                    />
                    <PasswordRequirementsValidator
                        password={newPassword}
                        passwordConfirmation={newPasswordConfirm}
                        onStateChange={onValidatorStateChange}
                    />
                    <Button
                        css={css`
                            margin-top: 3.333rem;
                        `}
                        onClick={onSubmitButtonClick}
                        disabled={disableSubmitButton}
                    >
                        {savePasswordLabel}
                    </Button>
                </>
            )}
        </MyAccountLayout>
    );
}

export default ChangePasswordDesktop;
